/**
 * Pure function port of the NestJS CrosstabQuestionMapper class.
 * Maps denormalized Snowflake question/option rows into CrosstabQuestionDto[].
 */

function stripHtml(text: string): string {
    return text
        .replace(/<[^>]+>/g, '')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&nbsp;/g, ' ')
        .replace(/&#?\w+;/g, '')
        .trim();
}

export interface CrosstabQuestionOptionDto {
    option_id: string[];
    sort_order: number;
    option: string;
    is_deletable?: boolean;
    question_group?: string;
    /** True for OE net/subnet rows; used by computeFromApi to apply kind='net' styling. */
    isNet?: boolean;
    /** When true, this option is excluded from row output by computeFromApi. */
    hidden?: boolean;
}

export interface CrosstabQuestionDto {
    id: string;
    qid: string;
    /** When two surveys share the same QUESTION_ID but differ in text/options,
     *  each variant gets a unique qid (e.g. "Q1", "Q1__v2"). This field
     *  always holds the original Snowflake column name used for response matching. */
    responseColumnId?: string;
    question: string;
    /**
     * M4S-5777 X24: the key waves of this question are merged by (`legacyWordingKey`), which was
     * the display text before labels were cleaned by one rule. A key, never shown. Absent on a
     * question that never had raw portal text (an uploaded open end): `question` is its key.
     */
    wordingKey?: string;
    sortOrder: number;
    questionType: string;
    options: CrosstabQuestionOptionDto[];
    min?: number | null;
    max?: number | null;
    /** Scale type from survey_question_config (e.g. 'percentage', 'integer'). Only set for intensity_scale questions. */
    scaleType?: string | null;
    matrixOptions: Record<string, string[]>;
    /** Survey IDs this question appears in (R-E1: cross-survey mismatch surfacing). */
    surveyIds?: string[];
    /**
     * M4S-5777 (defect 5). On a wave variant only: the answer tokens its waves use for another
     * answer than the base question (the catalog question whose qid is the column) lists under
     * them. A filter, variable or weight on the base column does not count them in this variant's
     * surveys (wave-columns.ts), so a pick from the base's own option list never counts another
     * wave's answer under its label. Absent when every shared code means the same answer.
     */
    baseCodesApart?: string[];
    /**
     * Dense 0-based position in the project's survey order, assigned by `assignSurveyOrder`.
     *
     * `sortOrder` is per-survey and collides across waves, so it cannot order a multi-survey
     * project on its own. This field is the one the crosstab sorts rows by. Absent on payloads
     * built before it existed, and on fixtures — every reader falls back to `sortOrder`.
     */
    surveyOrder?: number;
    /** True for coded open-end questions uploaded via OE upload. */
    isOpenEnd?: boolean;
    /** M4S-5777 (X61). On a ranked question only: the surveys whose answers carry ranks, so the
     *  pivot adds rank tokens for those waves (`rankedPivot`, ranked.ts). */
    rankedSurveyIds?: string[];
    /** M4S-5618. Set by the Postgres mapper on matrix questions only. Absent on fixtures, test
     *  builders and the legacy formatter. */
    matrixAxes?: CrosstabMatrixAxesDto;
    /**
     * M4S-5777 (X58). On a choice or collapsed question from the Postgres mapper: single or
     * multi-select, from `question_config.multiple` (`select-mode.ts`). Multi when any merged wave
     * was. Absent on fixtures, the legacy formatter and every other kind.
     */
    selectMode?: 'single' | 'multi';
    /**
     * M4S-5777 (X63). On an allocation (constant-sum) question only: the amount each respondent
     * shares out across the items, as the survey stores it (`allocation.ts`). Absent when the survey
     * does not say.
     */
    allocationTotal?: number;
}

/** A matrix statement or answer choice as the survey defines it. */
export interface CrosstabMatrixAxisItemDto {
    /** Upper-cased survey code: option_key for a statement; matrix_key, or the scale value on an
     *  intensity scale matrix, for an answer choice. */
    key: string;
    /** Survey text. */
    label: string;
}

export interface CrosstabMatrixAxesDto {
    /** Survey order (option_sort). */
    statements: CrosstabMatrixAxisItemDto[];
    /** Survey order (matrix_sort), or min…max on an intensity scale matrix. */
    choices: CrosstabMatrixAxisItemDto[];
}

/** The token a respondent holds for statement × answer choice
 *  (postgres-response-repository.ts:43-67). */
export const matrixCellId = (statementKey: string, choiceKey: string): string =>
    `${statementKey}_${choiceKey}`;

export interface ISnowflakeQuestionRow {
    SURVEY_ID: string | number | null;
    QUESTION_ID: string;
    SURVEY_NAME: string | null;
    SURVEY_QUESTION: string | null;
    SURVEY_QUESTION_ID: string | number | null;
    SURVEY_QUESTION_SORT_ORDER: number | null;
    QUESTION_EXTENSION: string | null;
    SURVEY_QUESTION_OPTION_KEY: string | null;
    QUESTION_SUB_TYPE: string | null;
    SPECIAL_QUESTION_TYPE: string | null;
    OPTION: string | null;
    SURVEY_QUESTION_OPTION_SORT_ORDER: number | null;
    SURVEY_MATRIX_OPTION_SORT_ORDER: number | null;
    SCALE_MIN: number | string | null;
    SCALE_MAX: number | string | null;
}

/**
 * Working copy of a question while options/matrix-options are being merged.
 */
interface IQuestionAccumulator {
    id: string;
    qid: string;
    question: string;
    sortOrder: number;
    questionType: string;
    options: ICrosstabAccumulatorOption[];
    min: number | null;
    max: number | null;
    matrixOptions: Record<string, string[]>;
}

interface ICrosstabAccumulatorOption extends CrosstabQuestionOptionDto {
    question_group?: string;
}

const INTENSITY_SCALE_SUB_TYPES = new Set(['intensity_scale', 'radio intensity scale']);

function upper(value: string | null | undefined): string | null {
    if (value === null || value === undefined) return null;
    return value.toString().toUpperCase();
}

function asString(value: string | number | null | undefined): string | null {
    if (value === null || value === undefined) return null;
    return value.toString();
}

function toNumber(value: number | string | null | undefined): number | null {
    if (value === null || value === undefined || value === '') return null;
    const n = typeof value === 'number' ? value : parseFloat(value as string);
    return Number.isFinite(n) ? n : null;
}

function optionExists(acc: IQuestionAccumulator, optionId: string): boolean {
    return acc.options.some((o) => o.option_id.includes(optionId));
}

function expandIntensityScaleNets(
    acc: IQuestionAccumulator,
    scaleMin: number,
    scaleMax: number,
): void {
    const minVal = parseInt(String(scaleMin), 10);
    const maxVal = parseInt(String(scaleMax), 10);
    if (Number.isNaN(minVal) || Number.isNaN(maxVal)) return;

    if (maxVal > 10) {
        const interval = Math.ceil(maxVal / 10);
        let sort = minVal === 0 ? Math.ceil(maxVal / interval) : Math.ceil(maxVal / interval) - 1;
        for (let i = minVal; i <= maxVal; i += interval) {
            const upper2 = i + interval - 1 < maxVal ? i + interval - 1 : maxVal;
            const optionId = `scale-${i}-${upper2}`;
            if (!optionExists(acc, optionId)) {
                acc.options.push({
                    option_id: [optionId],
                    sort_order: sort,
                    option: `${i} - ${upper2}`,
                });
                sort--;
            }
        }
        return;
    }

    // Display order: Top 2 Box first, then options descending (high → low), Bottom 2 Box last.
    // sort_order matches this visual order so UIs that sort by it stay consistent.
    let sort = minVal === 0 ? maxVal + 2 : maxVal + 1;

    // Top 2 Box (highest two scale codes) — appears first/at top
    const topTwo = `scale-${maxVal - 1}-${maxVal}`;
    if (!optionExists(acc, topTwo)) {
        acc.options.push({
            option_id: [topTwo],
            sort_order: sort,
            option: `Top 2 Box (${maxVal - 1} & ${maxVal})`,
        });
        sort--;
    }

    // Individual scale values in descending order (e.g. 5, 4, 3, 2, 1)
    for (let i = maxVal; i >= minVal; i--) {
        const idStr = `${i}`;
        if (!optionExists(acc, idStr)) {
            acc.options.push({
                option_id: [idStr],
                sort_order: sort,
                option: idStr,
            });
            sort--;
        }
    }

    // Bottom 2 Box (lowest two scale codes) — appears last/at bottom
    const bottomTwo = `scale-${minVal}-${minVal + 1}`;
    if (!optionExists(acc, bottomTwo)) {
        acc.options.push({
            option_id: [bottomTwo],
            sort_order: sort,
            option: `Bottom 2 Box (${minVal} & ${minVal + 1})`,
        });
    }
}

function applyOpenEndOption(
    acc: IQuestionAccumulator,
    extension: string,
    optionKey: string,
    optionLabel: string | null,
    optionSort: number,
): void {
    // "None of the above" must never be folded into a parent NET's option_id list.
    // Every respondent selects either a real option or NOBA, so including NOBA inflates
    // the NET count to 100% of the column base.
    const isNoba = /^none\b/i.test((optionLabel ?? '').trim());

    const existingNet = acc.options.find((o) => o.option === extension);
    if (!existingNet) {
        if (!isNoba) {
            acc.options.push({
                option_id: [optionKey],
                sort_order: optionSort,
                option: extension,
                is_deletable: false,
            });
        }
    } else if (!existingNet.option_id.includes(optionKey) && !isNoba) {
        existingNet.option_id.push(optionKey);

        const isSubnet = (existingNet.option ?? '').toLowerCase().includes('subnet');
        if (isSubnet) {
            const parentNet = acc.options.find((o) => o.option === existingNet.question_group);
            if (parentNet) parentNet.option_id.push(optionKey);
        }
    }

    if (optionLabel && acc.options.every((o) => o.option !== optionLabel)) {
        const isSubnet = optionLabel.toLowerCase().includes('subnet');
        acc.options.push({
            option_id: [optionKey],
            sort_order: optionSort,
            option: optionLabel,
            question_group: extension,
            is_deletable: !isSubnet,
        });
    }
}

function applyRow(byQid: Map<string, IQuestionAccumulator>, row: ISnowflakeQuestionRow): void {
    const qid = row.QUESTION_ID;
    if (!qid) return;

    const optionKey = upper(row.SURVEY_QUESTION_OPTION_KEY);
    const subType = row.QUESTION_SUB_TYPE ?? '';
    const specialType = row.SPECIAL_QUESTION_TYPE ?? '';
    const extension = row.QUESTION_EXTENSION ?? '';
    const optionLabel = row.OPTION ?? null;
    const optionSort = toNumber(row.SURVEY_QUESTION_OPTION_SORT_ORDER) ?? 0;
    const scaleMin = toNumber(row.SCALE_MIN);
    const scaleMax = toNumber(row.SCALE_MAX);

    let acc = byQid.get(qid);
    if (!acc) {
        acc = {
            id: asString(row.SURVEY_QUESTION_ID) ?? '',
            qid,
            question: stripHtml(row.SURVEY_QUESTION ?? ''),
            sortOrder: toNumber(row.SURVEY_QUESTION_SORT_ORDER) ?? 0,
            questionType: subType,
            options: [],
            min: scaleMin,
            max: scaleMax,
            matrixOptions: {},
        };
        byQid.set(qid, acc);
    }

    if ((subType === 'matrix' || subType === 'grid') && extension && optionKey && optionLabel) {
        const group = acc.matrixOptions[extension] ?? (acc.matrixOptions[extension] = []);
        group.push(optionKey);
    }

    if (INTENSITY_SCALE_SUB_TYPES.has(subType) && scaleMin !== null && scaleMax !== null) {
        expandIntensityScaleNets(acc, scaleMin, scaleMax);

        if (optionKey && optionLabel && !optionExists(acc, optionKey)) {
            const maxSort = acc.options.reduce((s, o) => (o.sort_order > s ? o.sort_order : s), 0);
            acc.options.push({
                option_id: [optionKey],
                sort_order: maxSort + 1,
                option: optionLabel,
            });
        }
    }

    if (
        subType === 'intensity scale matrix' &&
        scaleMin !== null &&
        scaleMax !== null &&
        optionLabel &&
        optionKey
    ) {
        const minVal = parseInt(String(scaleMin), 10);
        const maxVal = parseInt(String(scaleMax), 10);
        if (!Number.isNaN(minVal) && !Number.isNaN(maxVal)) {
            const group = acc.matrixOptions[optionLabel] ?? (acc.matrixOptions[optionLabel] = []);
            for (let i = minVal; i <= maxVal; i++) {
                const matrixId = `${optionKey}_${i}`;
                if (!group.includes(matrixId)) group.push(matrixId);
                if (!optionExists(acc, matrixId)) {
                    acc.options.push({
                        option_id: [matrixId],
                        sort_order: optionSort,
                        option: `${i}`,
                    });
                }
            }
        }
    }

    if (specialType === 'open_end' && extension && optionKey) {
        applyOpenEndOption(acc, extension, optionKey, optionLabel, optionSort);
    }

    if (
        optionLabel &&
        optionKey &&
        !optionExists(acc, optionKey) &&
        !['intensity_scale', 'intensity scale matrix', 'radio intensity scale'].includes(subType)
    ) {
        acc.options.push({
            option_id: [optionKey],
            sort_order: optionSort,
            option: optionLabel,
        });
    }
}

/**
 * Format Snowflake question rows into CrosstabQuestionDto[].
 * Pure function port of CrosstabQuestionMapper.format().
 */
export function formatQuestions(rows: ISnowflakeQuestionRow[]): CrosstabQuestionDto[] {
    const byQid = new Map<string, IQuestionAccumulator>();

    for (const row of rows) {
        applyRow(byQid, row);
    }

    return Array.from(byQid.values()).map((acc) => ({
        id: acc.id,
        qid: acc.qid,
        question: acc.question,
        sortOrder: acc.sortOrder,
        questionType: acc.questionType,
        options: acc.options.map(({ question_group: _qg, ...opt }) => opt),
        min: acc.min,
        max: acc.max,
        matrixOptions: acc.matrixOptions,
    }));
}
